import type { CallCodeDetails } from '../../interfaces/api/messageDetail/CallCodeDetails';
import type { ContextWrappingFunction } from '../../util/functions/ContextWrappingFunction';
import { getMessageCodesByPriority } from './getMessageDetailsByPriority';

const callCodeDefinitions: Record<string, CallCodeDetails> = {
	// Something terrible has happened
	CONTACT: {
		code: 'CONTACT',
		priority: 10000,
		message: 'Person in contact with equipment',
	},
	TRAPPED: {
		code: 'TRAPPED',
		priority: 9000,
		message: 'Person trapped',
	},

	// Police / fire are on scene
	WDPF: {
		code: 'WDPF',
		priority: 4900,
		message: 'Wire down, police / fire standing by',
	},
	STRUFIREPF: {
		code: 'STRUFIREPF',
		priority: 4800,
		message: 'Structure fire',
	},
	TRAFCSIGPF: {
		code: 'TRAFCSIGPF',
		priority: 4600,
		message: 'Traffic signal out, police / fire standing by',
	},
	MISCPF: {
		code: 'MISCPF',
		priority: 4500,
		message: 'Police / fire standing by',
	},

	// Power is out
	PPWD: {
		code: 'PPWD',
		priority: 3900,
		message: 'Pole to pole wire down',
	},
	PTFIRE: {
		code: 'PTFIRE',
		priority: 3800,
		message: 'Pole or transformer fire',
	},
	UGEXP: {
		code: 'UGEXP',
		priority: 3700,
		message: 'Underground wire exposed',
	},
	SDXL: {
		code: 'SDXL',
		priority: 3600,
		message: 'Wire down, power out',
	},
	XCURR: {
		code: 'XCURR',
		priority: 3500,
		message: 'Power out',
	},

	// There's a problem with the quality of the power
	ONELEG: {
		code: 'ONELEG',
		priority: 2400,
		message: 'Partial power',
	},
	INTERRUPTIBLE: {
		code: 'INTERRUPTIBLE',
		priority: 2300,
		message: 'Interruptible meter',
	},
	HIGHVOL: {
		code: 'HIGHVOL',
		priority: 2200,
		message: 'High voltage',
	},
	LOWVOL: {
		code: 'LOWVOL',
		priority: 2100,
		message: 'Low voltage',
	},

	// Something else isn't right
	SDLTSOK: {
		code: 'SDLTSOK',
		priority: 1600,
		message: 'Wire down',
	},
	ARC: {
		code: 'ARC',
		priority: 1500,
		message: 'Arcing',
	},
	TT: {
		description: 'Tree Trim',
		code: 'TT',
		priority: 1400,
		message: 'Tree trim',
	},
	BP: {
		code: 'BP',
		priority: 1300,
		message: 'Broken pole',
	},
	FLICKER: {
		code: 'FLICKER',
		priority: 1200,
		message: 'Flickering lights',
	},
	HZ: {
		code: 'HZ',
		priority: 1100,
		message: 'Hazard',
	},
	NONHZD: {
		code: 'NONHZD',
		priority: 100,
		message: 'Other non-hazard',
	},

	// Stuff that we only display and don't take in reports
	MULTIARC: {
		code: 'MULTIARC',
		message: 'Multi arc',
	},
	AMI_XCURR: {
		code: 'AMI_XCURR',
		message: 'Power out',
	},
	OPL: {
		code: 'OPL',
		message: 'Outdoor protective lighting',
	},
	SLO: {
		code: 'SLO',
		message: 'Street light out',
	},
	MOMENTARY: {
		code: 'MOMENTARY',
		message: 'Momentary outage',
	},
	SCHOOL: {
		code: 'SCHOOL',
		message: 'Power out, school',
	},
	PRIMARY: {
		code: 'PRIMARY',
		message: 'Power out',
	},
	HOSP: {
		code: 'HOSP',
		message: 'Power out, hospital',
	},
	EHS: {
		code: 'EHS',
		message: 'Power out, essential health and safety',
	},
	HIC: {
		code: 'HIC',
		message: 'Power out, high impact',
	},
	PREFLT: {
		code: 'PREFLT',
		message: 'Field work',
	},
	METERCNT: {
		code: 'METERCNT',
		message: 'Meter connect / disconnect',
	},
	METEREX: {
		code: 'METEREX',
		message: 'Meter exchange',
	},
	METEREXG: {
		// TODO: is this actually used or is it just METEREX?
		code: 'METEREXG',
		message: 'Meter exchange',
	},
	COLLECT: {
		code: 'COLLECT',
		message: 'Field work',
	},
	FOLLUP: {
		code: 'FOLLUP',
		message: 'Follow up',
	},
	PLA: {
		code: 'PLA',
		message: 'Public lighting authority',
	},
};

export function getAllCallCodes(_wrappedContext: ContextWrappingFunction): Record<string, CallCodeDetails> {
	// TODO: store values in DB
	return callCodeDefinitions;
}

export function getReportableCallCodes(): string[] {
	return getMessageCodesByPriority(callCodeDefinitions);
}

export function getCallCodeDetails(
	wrappedContext: ContextWrappingFunction,
	code: string | undefined,
): CallCodeDetails | undefined {
	if (!code) {
		return undefined;
	}

	// TODO: store values in DB
	const callCodeDetails = callCodeDefinitions[code];
	if (!callCodeDetails) {
		wrappedContext.error(`Missing details for call code: [ ${code} ]`);
		return undefined;
	}

	const returnDetails: CallCodeDetails = {
		code: callCodeDetails.code,
		message: callCodeDetails.message,
		details: callCodeDetails.details,
		icon: callCodeDetails.icon,
		style: callCodeDetails.style,
	};

	return returnDetails;
}
